/**
 * Session Timeline Visualization (Phase 2 — Step 2.11).
 *
 * Horizontal colored block timeline showing all sessions in the term.
 */

import React from 'react';
import { computeTotalSessions, getPastSessionCount } from '@abs/shared-logic';
import { AbsenceType, SessionOverrideType } from '@abs/shared-types';
import type { AbsenceEntry, SessionOverride, Course } from '@abs/shared-types';

interface SessionTimelineProps {
  course: Course;
  entries: AbsenceEntry[];
}

interface TimelineBlock {
  index: number;
  date: string;
  status: 'present' | 'unexcused' | 'excused' | 'late' | 'early_departure' | 'cancelled' | 'holiday' | 'future';
  label: string;
}

export function SessionTimeline({ course, entries }: SessionTimelineProps): React.ReactElement {
  const { total, sessions } = computeTotalSessions(
    course.meetingPatterns,
    course.sessionOverrides,
    course.startDate,
    course.endDate,
  );

  const pastCount = getPastSessionCount(sessions);
  const entriesByDate = new Map(entries.map((e) => [e.date, e]));
  const overridesByDate = new Map(course.sessionOverrides.map((o) => [o.date, o]));

  const blocks: TimelineBlock[] = sessions.map((session, index) => {
    const override: SessionOverride | undefined = overridesByDate.get(session.date);
    const entry: AbsenceEntry | undefined = entriesByDate.get(session.date);

    let status: TimelineBlock['status'] = 'future';
    let label = `Session ${index + 1}: `;

    if (override?.type === SessionOverrideType.Holiday || override?.type === SessionOverrideType.Cancelled) {
      status = 'holiday';
      label += override.type === SessionOverrideType.Holiday ? 'Holiday' : 'Cancelled';
    } else if (entry) {
      switch (entry.type) {
        case AbsenceType.Present:
          status = 'present';
          label += 'Present';
          break;
        case AbsenceType.Unexcused:
          status = 'unexcused';
          label += 'Unexcused absence';
          break;
        case AbsenceType.Excused:
          status = 'excused';
          label += 'Excused absence';
          break;
        case AbsenceType.Late:
          status = 'late';
          label += `Late (arrived ${entry.arrivalTime ?? '—'})`;
          break;
        case AbsenceType.EarlyDeparture:
          status = 'early_departure';
          label += `Early departure (${entry.departureTime ?? '—'})`;
          break;
        case AbsenceType.Cancelled:
          status = 'cancelled';
          label += 'Cancelled';
          break;
      }
    } else if (index < pastCount) {
      status = 'present';
      label += 'Present (no entry)';
    } else {
      label += 'Upcoming';
    }

    return { index, date: session.date, status, label };
  });

  return (
    <div className="session-timeline" role="img" aria-label={`Session timeline: ${total} sessions`}>
      <div className="session-timeline__track">
        {blocks.map((block) => (
          <div
            key={block.index}
            className={`session-timeline__block session-timeline__block--${block.status}`}
            title={block.label}
            aria-label={block.label}
          />
        ))}
      </div>
      <div className="session-timeline__legend">
        <span className="session-timeline__legend-item session-timeline__block--present">Present</span>
        <span className="session-timeline__legend-item session-timeline__block--unexcused">Unexcused</span>
        <span className="session-timeline__legend-item session-timeline__block--excused">Excused</span>
        <span className="session-timeline__legend-item session-timeline__block--late">Late</span>
        <span className="session-timeline__legend-item session-timeline__block--future">Future</span>
      </div>
    </div>
  );
}
